/**
 * Number Input with Commas and Plus/Minus Buttons Renderer
 * Automatically formats numbers with commas as the user types
 * Provides increment/decrement buttons for quick value adjustment
 * Stores the numeric value (not the formatted string) in form data
 */
import React, { useState, useEffect } from 'react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { TooltipWrapper } from '@/components/ui/tooltip-wrapper';
import { Plus, Minus } from 'lucide-react';
import type { FieldRenderer } from '@/MyCode/core';

export const renderNumberInputWithCommasPlusMinusCompact: FieldRenderer = ({
    field,
    value,
    hasError,
    handleChange,
    renderIcon,
    isRequired
}) => {
    // Default increment values from field config or default
    const incrementValues = (field as any).incrementValues || [100, 10, 1];
    const allowNegative = (field as any).allowNegative || false;
    const min = field.minValue !== undefined ? Number(field.minValue) : undefined;
    const max = field.maxValue !== undefined ? Number(field.maxValue) : undefined;

    // Format number with commas, preserving decimal points
    const formatNumberWithCommas = (numValue: number | string): string => {
        if (!numValue || numValue === '') return '';

        let numStr: string;
        if (typeof numValue === 'string') {
            // Remove all non-digit and non-decimal characters (allow one decimal point)
            // Split by decimal point to handle both parts
            const parts = numValue.split('.');
            const integerPart = parts[0].replace(/\D/g, '');
            const decimalPart = parts.length > 1 ? '.' + parts[1].replace(/\D/g, '') : '';
            numStr = integerPart + decimalPart;
        } else {
            numStr = numValue.toString();
        }

        // Split by decimal point if exists
        const parts = numStr.split('.');
        const integerPart = parts[0];
        const decimalPart = parts.length > 1 ? '.' + parts[1] : '';

        // Format integer part with commas
        const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

        return formattedInteger + decimalPart;
    };

    // Initialize display value from numeric value
    const [displayValue, setDisplayValue] = useState<string>(() => {
        if (value !== undefined && value !== null && value !== '') {
            const numVal = typeof value === 'number' ? value : Number(value);
            if (!isNaN(numVal)) {
                return formatNumberWithCommas(numVal);
            }
        }
        return '';
    });

    // Update display value when external value changes
    useEffect(() => {
        if (value !== undefined && value !== null && value !== '') {
            const numericVal = typeof value === 'number' ? value : Number(value);
            if (!isNaN(numericVal)) {
                setDisplayValue(formatNumberWithCommas(numericVal));
            }
        } else if (value === '' || value === null || value === undefined) {
            setDisplayValue('');
        }
    }, [value]);

    // Update value with clamping
    const updateValue = (newNumeric: number) => {
        let clamped = newNumeric;

        if (!allowNegative) {
            clamped = Math.max(0, clamped);
        }
        if (min !== undefined) {
            clamped = Math.max(min, clamped);
        }
        if (max !== undefined) {
            clamped = Math.min(max, clamped);
        }

        setDisplayValue(formatNumberWithCommas(clamped));
        handleChange(field.name, clamped);
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const inputValue = e.target.value;

        // Allow digits and one decimal point
        // Remove all non-digit and non-decimal characters, but preserve decimal point
        const sanitized = inputValue.replace(/[^\d.]/g, '');

        // Ensure only one decimal point
        const parts = sanitized.split('.');
        let cleaned = parts[0].replace(/\D/g, '');
        if (parts.length > 1) {
            cleaned += '.' + parts.slice(1).join('').replace(/\D/g, '');
        }

        // Parse as float to handle decimals
        const numeric = cleaned && cleaned !== '.' ? parseFloat(cleaned) : 0;

        // Format for display (preserve decimal input)
        const formatted = formatNumberWithCommas(cleaned || '');

        setDisplayValue(formatted);

        // Update with clamping (only if we have a valid number)
        if (!isNaN(numeric)) {
            updateValue(numeric);
        }
    };

    // Helper function to get the maximum decimal places from increment values
    const getMaxDecimalPlaces = (values: number[]): number => {
        let maxPlaces = 0;
        values.forEach(val => {
            const str = val.toString();
            const decimalIndex = str.indexOf('.');
            if (decimalIndex !== -1) {
                const places = str.length - decimalIndex - 1;
                maxPlaces = Math.max(maxPlaces, places);
            }
        });
        return maxPlaces;
    };

    // Get maximum decimal places from increment values
    const maxDecimalPlaces = getMaxDecimalPlaces(incrementValues);

    const adjustValue = (amount: number) => {
        // Remove commas and parse as float to handle decimals
        const cleaned = displayValue.replace(/,/g, '') || '0';
        const currentNumeric = parseFloat(cleaned);

        if (!isNaN(currentNumeric)) {
            // Calculate new value with proper decimal precision
            // Use multiplication/division to avoid floating-point precision issues
            const multiplier = Math.pow(10, maxDecimalPlaces);
            const newValue = Math.round((currentNumeric * multiplier + amount * multiplier)) / multiplier;
            updateValue(newValue);
        }
    };

    return (
        <div className="space-y-2 grid h-full">
            <Label htmlFor={field.name} className="grid h-[75%] items-start">
                {field.label}
                {isRequired && <span className="align-top grid text-destructive ml-1">*</span>}
            </Label>
            <div className="flex flex-1 items-start gap-3">
                {renderIcon(field.ui?.icon)}
                <div className="flex flex-1 gap-3 sm:flex-row flex-col">
                    <Input
                        id={field.name}
                        type="text"
                        value={displayValue}
                        onChange={handleInputChange}
                        placeholder={field.placeholder || '0'}
                        className={`text-lg font-semibold flex-1 xs:flex ${hasError ? 'border-destructive' : ''}`}
                    />

                    <div className="flex w-1/2 flex-col gap-1.5 text-sm">
                        <div className="flex gap-1.5">
                            {incrementValues.map((val: number) => (
                                <div className='flex flex-col items-center'>
                                    <Button
                                        type="button"
                                        onClick={() => adjustValue(val)}
                                        variant="outline"
                                        size="sm"
                                        className="h-5 px-0 text-xs whitespace-nowrap bg-green-300/40 !rounded-t-md"
                                    >
                                        <Plus className="h-2 w-2 mr-0" />
                                        {val}
                                    </Button>
                                    <Button
                                        type="button"
                                        onClick={() => adjustValue(-val)}
                                        variant="outline"
                                        size="sm"
                                        className="h-5 px-0 text-xs whitespace-nowrap bg-red-300/40 !rounded-b-md"
                                    >
                                        <Minus className="h-2 w-2 mr-0" />
                                        {val}
                                    </Button>
                                </div>

                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default renderNumberInputWithCommasPlusMinusCompact;

